import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Portal from "./pages/Portal";
import PickupLogin from "./pickup/pages/PickupLogin";
import PickupChildren from "./pickup/pages/PickupChildren";
import PickupTeacher from "./pages/PickupTeacher";
import PickupAdmin from "./pages/PickupAdmin";
import PickupDuty from "./pages/PickupDuty";
import Circulars from "./pages/Circulars";
import Dashboard from "./pages/Dashboard";
import NewStudentForm from "./pages/NewStudentForm";
import WithdrawnStudentForm from "./pages/WithdrawnStudentForm";
import NewStudentsList from "./pages/NewStudentsList";
import WithdrawnStudentsList from "./pages/WithdrawnStudentsList";
import Reports from "./pages/Reports";
import UserManagement from "./pages/UserManagement";
import ExcelImport from "./pages/ExcelImport";
import Attendance from "./pages/Attendance";
import AttendanceReports from "./pages/AttendanceReports";
import Promotion from "./pages/Promotion";
import LessonPlanForm from "./pages/LessonPlanForm";
import LessonPlansList from "./pages/LessonPlansList";
import WhatsAppCompose from "./pages/WhatsAppCompose";
import WhatsAppLog from "./pages/WhatsAppLog";
import LibraryRoutes from "./library/LibraryRoutes";

function Shell({ children, roles }) {
  return (
    <ProtectedRoute allowedRoles={roles}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function PortalShell() {
  // Portal is PUBLIC — first thing visitors see. Auth is only required
  // when they choose a specific system.
  return <Portal />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-center" dir="rtl" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/pickup/login" element={<PickupLogin />} />
            <Route path="/pickup/children" element={<PickupChildren />} />

            {/* Portal — landing after login */}
            <Route path="/" element={<PortalShell />} />

            {/* Library — sub-app mounted at /library/* (public read, auth for admin) */}
            <Route path="/library/*" element={<LibraryRoutes />} />

            {/* Student ERP */}
            <Route path="/students" element={<Shell><Dashboard /></Shell>} />
            <Route path="/new" element={<Shell><NewStudentsList /></Shell>} />
            <Route path="/new/register" element={<Shell roles={["admin", "student_affairs"]}><NewStudentForm /></Shell>} />
            <Route path="/withdrawn" element={<Shell><WithdrawnStudentsList /></Shell>} />
            <Route path="/withdrawn/register" element={<Shell roles={["admin", "student_affairs"]}><WithdrawnStudentForm /></Shell>} />
            <Route path="/attendance" element={<Shell roles={["admin", "student_affairs", "teacher"]}><Attendance /></Shell>} />
            <Route path="/attendance/reports" element={<Shell roles={["admin", "supervisor", "student_affairs", "teacher", "student_guide"]}><AttendanceReports /></Shell>} />
            <Route path="/lesson-plans" element={<Shell roles={["admin", "supervisor", "student_affairs", "teacher", "student_guide"]}><LessonPlansList /></Shell>} />
            <Route path="/pickup/teacher" element={<Shell><PickupTeacher /></Shell>} />
            <Route path="/pickup/admin" element={<Shell><PickupAdmin /></Shell>} />
            <Route path="/pickup/duty" element={<Shell><PickupDuty /></Shell>} />
            <Route path="/circulars" element={<Shell><Circulars /></Shell>} />
            <Route path="/lesson-plans/new" element={<Shell roles={["admin", "teacher"]}><LessonPlanForm /></Shell>} />
            <Route path="/lesson-plans/:id/edit" element={<Shell roles={["admin", "teacher"]}><LessonPlanForm /></Shell>} />
            <Route path="/whatsapp/compose" element={<Shell roles={["super_admin", "admin", "student_affairs", "student_guide"]}><WhatsAppCompose /></Shell>} />
            <Route path="/whatsapp/logs" element={<Shell roles={["super_admin", "admin", "supervisor", "student_affairs", "student_guide"]}><WhatsAppLog /></Shell>} />
            <Route path="/reports" element={<Shell roles={["admin", "supervisor", "student_affairs"]}><Reports /></Shell>} />
            <Route path="/import" element={<Shell roles={["admin"]}><ExcelImport /></Shell>} />
            <Route path="/promote" element={<Shell roles={["admin"]}><Promotion /></Shell>} />
            <Route path="/users" element={<Shell roles={["admin"]}><UserManagement /></Shell>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
